<template>
  <div>
    <Card
      v-bind="$attrs"
      :clickable="isClickable"
      :selected="selected"
      :href="profileUrl"
      :target="target"
      :custom-classes="customClasses"
    >
      <AlertBanner v-if="availability" :status="validStatus" :auto-scroll-to-banner="false">
        <template #icon>
          <CalendarIcon />
        </template>
        Available: {{ availabilityText }}
      </AlertBanner>
      <AlertBanner v-if="shouldShowAvailabilityAlert" class="mt-8 mb-16" :status="6" :auto-scroll-to-banner="false">
        <div>You invited this sitter to apply</div>
      </AlertBanner>
      <div v-if="isLoading"><SitterCardLoadingSkeleton /></div>
      <div v-else>
        <div class="flex flex-col">
          <div class="w-full flex flex-row">
            <div class="w-full pr-16 overflow-hidden">
              <h2 class="text-h3 text-ellipsis overflow-hidden whitespace-nowrap">{{ name }}</h2>
              <p v-if="professionalExperienceDisplay" class="text-body">{{ professionalExperienceDisplay }}</p>
              <p v-if="informalExperienceDisplay" class="text-body">{{ informalExperienceDisplay }}</p>
              <p class="text-body">{{ locationText }}</p>
              <p v-if="hasRate" class="text-body">
                <span class="profile-card__rate-unit">$</span>
                <span class="profile-card__rate-value">{{ computedMin }}–{{ computedMax }}</span
                >/hr
              </p>
              <StarRating v-if="totalRatings > 0" :count="totalRatings || 0" :average="avgRating" />
            </div>
            <div>
              <a v-if="photoUploadAvatar" class="relative" aria-label="Photo upload" href="/sitter/photo_upload#/sitter_upload">
                <Avatar photo-upload-icon :size="AvatarSize.MEGA" name="" empty-alt />
                <div class="flex justify-center items-center w-32 h-32 absolute top-14 right-0" aria-label="Add profile photo">
                  <AddFilled />
                </div>
              </a>
              <Avatar v-else :name="name" :image-url="photoUrl" :size="AvatarSize.MEGA" />
            </div>
          </div>
          <p
            v-if="truncatedDescription"
            id="sitter-card-description"
            class="text-small border-t-2 border-solid border-grey-divider-subdued pt-4"
          >
            {{ truncatedDescription }}
          </p>
          <div class="flex flex-wrap items-center gap-y-12 md:gap-16 mt-16">
            <SitterBadgeList :include-inactive-badges="false" :badge-size="40" :idv="idv" :badges="visibleBadges" />
            <Button
              v-if="shouldShowInviteButton"
              value="Invite to Apply"
              variant="primary"
              size="small"
              class="whitespace-nowrap ml-auto max-[410px]:ml-0 max-[410px]:w-full"
              @click="openInvitePopup"
            />
          </div>
        </div>
      </div>
    </Card>
    <!-- Popup is outside Card to prevent click propagation issues -->
    <RecruitSitterPopup
      v-if="account && (shouldShowInviteButton || showInvitePopup)"
      v-model="showInvitePopup"
      :account="account"
      :sitter-uuid="sitterIdForProfile"
      :sitter-name="name"
      :search-care-type="searchCareType || caretype"
      :parent-jobs="parentJobs"
      @sitter-recruited="handleSitterRecruited"
    />
  </div>
</template>

<script lang="ts">
import { parseISO, format } from 'date-fns';
import pluralize from 'pluralize';
import { PropType } from 'vue';
import { isChildCare } from '@/apps/provider/profile/__types__/CareTypes';
import helperStatus from '../../helpers/status';
import { IDV, Badge } from '../sitter_badge_list/__types__/SitterBadgeListTypes';

// Components
import AlertBanner from '../alert_banner/AlertBanner.vue';
import Avatar, { AvatarSize } from '../avatar/Avatar.vue';
import Card from '../card/Card.vue';
import SitterCardLoadingSkeleton from './SitterCardLoadingSkeleton.vue';
import StarRating from '../star_rating/StarRating.vue';
import SitterBadgeList from '../sitter_badge_list/SitterBadgeList.vue';
import Button from '../button/Button.vue';
import RecruitSitterPopup from '@/apps/search/features/RecruitSitterPopup.vue';

// Badges & Icons
import CalendarIcon from '../../icons/CalendarIcon.vue';
import AddFilled from '../../icons/AddFilled.vue';
import { LinkTarget } from '../../helpers/redirects';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import { Account } from '@/models/account';
import { JobEdge } from '@/graphql/search/queries';

const METERS_TO_MILES_CONVERSION = 1609.344;
const LIMIT_WORD_COUNT = 300;

export default {
  name: 'SitterCard',
  components: {
    AlertBanner,
    Avatar,
    Card,
    SitterCardLoadingSkeleton,
    StarRating,
    SitterBadgeList,
    CalendarIcon,
    AddFilled,
    Button,
    RecruitSitterPopup,
  },
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: '',
    },
    city: {
      type: String,
      default: '',
    },
    stateAbbr: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    avgRating: {
      type: Number,
      default: null,
    },
    minRate: {
      type: Number,
      default: null,
    },
    maxRate: {
      type: Number,
      default: null,
    },
    distance: {
      type: Number,
      default: null,
    },
    totalRatings: {
      type: Number || null,
      default: null,
    },
    photoUrl: {
      type: String,
      default: '',
    },
    yearsExperience: {
      type: Number || null,
      default: null,
    },
    informalYearsExperience: {
      type: Number || null,
      default: null,
    },
    idv: {
      type: Object as () => IDV,
      default: () => ({}),
    },
    badges: {
      type: Array as () => Badge[],
      default: () => [],
    },
    sitterIdForProfile: {
      type: String,
      required: false,
      default: null,
    },
    caretype: {
      type: String,
      required: false,
      default: 'babysitter',
    },
    availability: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    customClasses: {
      type: String,
      default: '',
      required: false,
    },
    photoUploadAvatar: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String as PropType<LinkTarget>,
      default: '_self',
    },
    account: {
      type: Object as PropType<Account | null>,
      default: null,
    },
    showInviteButton: {
      type: Boolean,
      default: false,
    },
    isRecruited: {
      type: Boolean,
      default: false,
    },
    searchCareType: {
      type: String,
      default: '',
    },
    parentJobs: {
      type: Array as PropType<JobEdge[]>,
      default: () => [],
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      AvatarSize,
      showInvitePopup: false,
      locallyRecruited: false,
    };
  },
  computed: {
    effectiveIsRecruited(): boolean {
      return this.isRecruited || this.locallyRecruited;
    },
    isClickable(): boolean {
      return !!this.sitterIdForProfile;
    },
    shouldShowAvailabilityAlert(): boolean {
      // Show banner when sitter is recruited and either on availability route or invite button is enabled
      return this.effectiveIsRecruited && (this.isAvailabilityRoute || this.showInviteButton);
    },
    isAvailabilityRoute(): boolean {
      return this.$route?.path === '/availability/search';
    },
    isChildCareProfile(): boolean {
      return isChildCare(this.caretype);
    },
    yearsExperiencePluralized(): string {
      const experience = this.yearsExperience;
      if (experience === 1) {
        return `${experience} year experience`;
      }

      return `${experience} years experience`;
    },
    professionalExperienceDisplay(): string {
      const experience = this.yearsExperience ?? 0;

      if (this.isChildCareProfile) {
        if (experience < 1) return '';
        return `${experience} ${pluralize('year', experience)} professional experience`;
      }

      if (!experience) return '';
      return this.yearsExperiencePluralized;
    },
    informalExperienceDisplay(): string {
      if (!this.isChildCareProfile) return '';

      const experience = this.informalYearsExperience ?? 0;
      if (experience < 1) return '';
      return `${experience} ${pluralize('yr', experience)} informal experience`;
    },
    locationText(): string {
      let locationDistanceSeparater = '';
      let cityStateSeparater = '';
      if ((this.city || this.stateAbbr) && this.computedDistance) locationDistanceSeparater = ' • ';
      if (this.city && this.stateAbbr) cityStateSeparater = ', ';
      return `${this.city}${cityStateSeparater}${this.stateAbbr}${locationDistanceSeparater}${this.computedDistance}`;
    },
    computedDistance(): string {
      if (typeof this.distance !== 'number') {
        return '';
      }

      const miles = Math.round(Number(this.distance) / METERS_TO_MILES_CONVERSION);
      if (miles < 1) return '< 1 mile';
      if (miles === 1) return '1 mile';
      return `${miles} miles`;
    },
    truncatedDescription(): string {
      if (!this.description) return '';
      const regex = new RegExp(`^(.{${LIMIT_WORD_COUNT}}[^\\s]*).*`);
      const truncated = this.description.replace(/(\r\n|\n|\r)/gm, '').replace(regex, '$1');
      return `${truncated}...`;
    },
    computedMin(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L38
      return Math.floor(this.minRate / 100);
    },
    computedMax(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L39
      return Math.ceil(this.maxRate / 100);
    },
    hasRate(): boolean {
      return this.computedMin > 0 && this.computedMax > 0;
    },
    profileUrl(): string | boolean {
      if (!this.sitterIdForProfile) {
        return false;
      }

      return getProviderProfileUrl(this.sitterIdForProfile, this.caretype) || false;
    },
    availabilityText(): string {
      const s = parseISO(this.availability.start);
      const e = parseISO(this.availability.end);
      return `${format(s, 'MMM d, h:mm aaa')} – ${format(e, 'h:mm aaa')}`;
    },
    validStatus(): helperStatus {
      return helperStatus.Valid;
    },
    visibleBadges(): Badge[] {
      // mobile fits 5 badges max - omit basic criminal badge if all badges + IDV are complete.
      const allBadgesComplete = this.badges.every((badge) => badge.status === 'COMPLETE');
      return allBadgesComplete && this.idv?.verdict === 'passed'
        ? this.badges.filter((badge) => badge.name !== 'basic_criminal')
        : this.badges;
    },
    shouldShowInviteButton(): boolean {
      return this.showInviteButton && !this.effectiveIsRecruited && !!this.account;
    },
  },
  methods: {
    openInvitePopup(event: Event) {
      event.preventDefault();
      event.stopPropagation();
      this.showInvitePopup = true;
    },
    handleSitterRecruited(sitterUuid: string | null = null) {
      this.locallyRecruited = true;
      this.$emit('sitterRecruited', sitterUuid || this.sitterIdForProfile || null);
    },
  },
};
</script>

<style lang="scss" scoped>
#sitter-card-description {
  word-wrap: break-word;
}
</style>
